import React, { useEffect, useState } from 'react';
import {
  Heading,
  NavigationalTab,
  NavigationalTabs,
  ProgressCircular,
} from '@americanexpress/dls-react';
import HotelImageCollage from '../common/HotelImageCollage';
import HotelDescription from '../common/HotelDescription';
import HotelCheckInOut from '../common/HotelCheckInOut';
import HotelSentimentCard from '../common/HotelSentimentCard';
import HotelFacilities from '../common/HotelFacilities';
import HotelRooms from '../common/HotelRooms';
import HotelReviews from '../common/HotelReviews';
import HotelPolicies from '../common/HotelPolicies';
import HotelImportantInformation from '../common/HotelImportantInformation';
import HotelLocationSection from '../common/HotelLocationSection';
import HotelStarRating from '../common/HotelStarRating';

const nuiteeHotelDetails = require('../../mocks/nuiteeHotelDetails');
const nuiteeHotelRates = require('../../mocks/nuiteeHotelRates');

const HOTEL_SECTION_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'location', label: 'Location' },
  { id: 'sentiment', label: 'Sentiment' },
  { id: 'facilities', label: 'Facilities' },
  { id: 'rooms', label: 'Rooms' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'policies', label: 'Policies' },
];

const extractHotelPhotos = (hotelData = {}) => {
  const hotelImages = hotelData?.hotelImages || [];
  const defaultImage = hotelImages.find((image) => image?.defaultImage);
  const heroPhoto = defaultImage?.urlHd || hotelImages[0]?.urlHd;
  const galleryPhotos = hotelImages
    .map((image) => image?.urlHd)
    .filter(Boolean)
    .filter((photo) => photo !== heroPhoto);

  return [...new Set([heroPhoto, ...galleryPhotos].filter(Boolean))];
};

const buildHotelLocation = (hotelData = {}) => {
  const coordinates = typeof hotelData?.location?.latitude === 'number'
    && typeof hotelData?.location?.longitude === 'number'
    ? {
      lat: hotelData.location.latitude,
      lng: hotelData.location.longitude,
    }
    : null;

  return {
    coordinates,
    addressLines: [
      hotelData?.address,
      hotelData?.city,
    ].filter(Boolean),
    phone: hotelData?.phone || null,
  };
};

const getRefundLabel = (refundableTag) => {
  if (refundableTag === 'RFN') return 'Refundable';
  if (refundableTag === 'NRFN') return 'Non-refundable';
  return null;
};

const buildRooms = (hotelData = {}, ratesData = []) => {
  const pricesByName = ratesData.reduce((acc, hotelRate) => {
    (hotelRate?.roomTypes || []).forEach((roomType) => {
      const firstRate = roomType?.rates?.[0];
      const total = firstRate?.retailRate?.total?.[0];
      const initialPrice = firstRate?.retailRate?.initialPrice?.[0];
      const cancellationPolicies = firstRate?.cancellationPolicies || {};
      const cancelPolicyInfos = cancellationPolicies?.cancelPolicyInfos || [];
      const freeCancellation = cancelPolicyInfos.find((p) => p?.amount === 0);

      if (roomType?.name && typeof total?.amount === 'number' && total?.currency) {
        acc[roomType.name] = {
          amount: total.amount,
          currency: total.currency,
          strikethroughAmount: initialPrice?.amount,
          strikethroughCurrency: initialPrice?.currency,
          boardLabel: firstRate?.boardName || firstRate?.boardType || null,
          refundLabel: getRefundLabel(cancellationPolicies?.refundableTag),
          freeCancellationUntil: freeCancellation?.cancelTime || null,
          paymentLabel: firstRate?.paymentTypes?.join(', ') || null,
        };
      }
    });
    return acc;
  }, {});

  return (hotelData?.rooms || []).map((room) => {
    const pricing = pricesByName[room.roomName];
    const bedSummary = (room.bedTypes || []).length > 0
      ? room.bedTypes.map((b) => `${b.quantity}\u00d7 ${b.bedType}`).join(', ')
      : null;
    const amenities = (room.roomAmenities || [])
      .map((amenity) => amenity?.name)
      .filter(Boolean);

    return {
      id: room.id,
      name: room.roomName,
      description: room.description,
      amenities,
      detailsVariant: 'modal',
      size: room.roomSizeSquare ? { value: room.roomSizeSquare, unit: room.roomSizeUnit } : null,
      maxOccupancy: room.maxOccupancy,
      bedSummary,
      photos: (room.photos || []).map((p) => ({ url: p.url, alt: p.imageDescription })),
      pricing,
    };
  });
};

const useNuiteeData = () => {
  const [data, setData] = useState();
  const [ratesData, setRatesData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setData({ body: nuiteeHotelDetails });
      setRatesData({ body: nuiteeHotelRates });
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timeout);
  }, []);

  return { data, error: null, isLoading, ratesData };
};

const NuiteeDetailsPage = () => {
  const { data, error, isLoading, ratesData } = useNuiteeData();

  if (isLoading) {
    return <ProgressCircular />;
  }

  if (error) {
    return (
      <Heading level={1} variant="sans-xlarge-book">
        Unable to load hotel details
      </Heading>
    );
  }

  const hotelData = data?.body?.data || {};
  const hotelName = hotelData?.name;
  const starRating = hotelData?.starRating;
  const city = hotelData?.city;
  const photos = extractHotelPhotos(hotelData);
  const location = buildHotelLocation(hotelData);
  const hotelMetaText = [
    city,
  ].filter(Boolean).join(' | ');

  const sentimentAnalysis = hotelData?.sentiment_analysis || {};
  const checkinTimes = hotelData?.checkinCheckoutTimes || {};
  const checkInText = [checkinTimes.checkin_start, checkinTimes.checkin_end]
    .filter(Boolean)
    .join(' \u2013 ') || null;

  const rooms = buildRooms(hotelData, ratesData?.body?.data ?? []);

  const policies = (hotelData?.policies || []).map((p) => ({
    description: p.description,
  }));

  const nonOverviewSections = HOTEL_SECTION_ITEMS
    .filter((s) => s.id !== 'overview' && s.id !== 'sentiment' && s.id !== 'location');

  return (
    <>
      <Heading level={1} variant="sans-xlarge-book">{hotelName}</Heading>
      <div className="flex flex-align-center margin-1-t">
        <HotelStarRating rating={starRating} />
        {hotelMetaText ? <p className="body margin-0 margin-1-l">{hotelMetaText}</p> : null}
      </div>
      <HotelImageCollage
        hotelName={hotelName}
        photos={photos}
        heroVideoUrl={hotelData?.videoUrl}
      />
      <NavigationalTabs className="margin-2-t" aria-label="Hotel detail sections">
        {HOTEL_SECTION_ITEMS.map((section, index) => (
          <NavigationalTab
            key={section.id}
            id={`tab-${section.id}`}
            href={`#${section.id}`}
            aria-current={index === 0 ? 'page' : undefined}
          >
            {section.label}
          </NavigationalTab>
        ))}
      </NavigationalTabs>

      <section id="overview" className="margin-4-t" style={{ scrollMarginTop: 'calc(64px + 1rem)' }}>
        <HotelDescription description={hotelData?.hotelDescription} />
        <HotelCheckInOut
          checkInText={checkInText}
          checkOutText={checkinTimes.checkout || null}
        />
      </section>

      <HotelLocationSection
        hotelName={hotelName}
        coordinates={location.coordinates}
        addressLines={location.addressLines}
        phone={location.phone}
      />

      <section id="sentiment" className="margin-4-t" style={{ scrollMarginTop: 'calc(64px + 1rem)' }}>
        <HotelSentimentCard
          pros={sentimentAnalysis?.pros || []}
          cons={sentimentAnalysis?.cons || []}
          updatedAt={hotelData?.sentiment_updated_at}
        />
      </section>

      {nonOverviewSections.map((section) => (
        <section key={section.id} id={section.id} className="margin-4-t" style={{ scrollMarginTop: 'calc(64px + 1rem)' }}>
          <Heading level={2} variant="sans-medium-bold">{section.label}</Heading>
          {section.id === 'facilities' && (
            <HotelFacilities facilities={hotelData?.hotelFacilities} />
          )}
          {section.id === 'rooms' && (
            <HotelRooms rooms={rooms} />
          )}
          {section.id === 'reviews' && (
            <HotelReviews
              rating={hotelData?.rating}
              ratingOutOf={10}
              reviewCount={hotelData?.reviewCount}
              categories={sentimentAnalysis?.categories}
            />
          )}
          {section.id === 'policies' && (
            <HotelPolicies policies={policies} />
          )}
        </section>
      ))}
      <section className="margin-4-t">
        <HotelImportantInformation
          content={hotelData?.hotelImportantInformation?.replace(/\n/g, '<br />')}
        />
      </section>    </>
  );
};

export default NuiteeDetailsPage;
